import {
  Button,
  Card,
  Checkbox,
  ConfirmDialog,
  Table,
  useToast,
  type TableRow,
} from '@citizen-walk/shared-ui';
import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useAdminAuth } from '../auth';
import { useDistrict } from '../districts-admin';
import { deleteRouteReportSnapshot } from './routeReportSnapshotActions';
import {
  computeCombinedHensachi,
  computeRouteCategoryDensities,
  computeRouteDensities,
  computeRouteQualityValues,
  type RouteQualityMetric,
} from './routeReportComparison';
import { RouteDensityBarChart, type RouteDensityBarChartRow } from './RouteDensityBarChart';
import { RouteComparisonMap } from './RouteComparisonMap';
import { REPORT_PALETTE } from './AreaComparisonBarChart';
import { SnapshotAggregationControls } from './SnapshotAggregationControls';
import {
  expandAxisCountsByPolarity,
  formatPolarityCounts,
  type AxisGranularity,
} from './computeAreaAxisCounts';
import { POLARITY_LABELS } from '@citizen-walk/shared-types';
import { useRecomputedSnapshots } from './useRecomputedSnapshots';
import { useStreetSurveySelection } from './streetSurveySurveys';
import { StreetSurveySurveysPanel } from './StreetSurveySurveysPanel';
import { DEFAULT_STREET_SURVEY_KEY } from '@citizen-walk/shared-types';
import { useRouteReportSnapshots, type RouteReportSnapshotWithId } from './useRouteReportSnapshots';

/**
 * 路線調査 比較ダッシュボード (docs/25 §10-6)。
 * `/districts/:districtId/street-survey/route-reports` — `StreetSurveyBatchPanel` の
 * 「路線として保存」で作成したスナップショットの一覧・削除・複数選択時の正規化比較を行う。
 *
 * 主指標は「1地点あたり平均検出件数」(`perPointAvg`、選択した路線群の中での位置づけを偏差値で
 * 示す)。当初 (MVP) は検出件数を路線距離(m)で正規化 (100mあたり件数) していたが、候補点の
 * 生成間隔 (`intervalM`、5〜500mでユーザーが自由設定可能) が路線間で異なると、間隔を狭く
 * 設定した路線ほど同じ距離でも地点数・検出件数が単純に増えてしまう不公平が判明した
 * （ユーザー指摘、2026-07-23）。§10-5のエリア比較が地点数で正規化しているのと同じ考え方に
 * 揃え、主指標・偏差値の算出元を地点数正規化に切り替えた (`routeReportComparison.ts`)。
 * 距離正規化 (100mあたり件数) は参考値として表に残す。基準値 (平均) は選択した路線群の合算
 * から算出する MVP方式 (地区の道路網総延長を使う精緻な方式は今回のスコープ外)。
 *
 * 比較グラフ (`RouteDensityBarChart`) は検出要素 (カテゴリ) ごとに色分けした積み上げ棒で表示する
 * (2026-07-23、当初は単色の棒だったが「検出要素ごとに色分けしたい」というユーザー要望で変更)。
 *
 * 偏差値は「質 (1地点あたり平均検出件数) だけでなく距離も大事」というユーザー指摘を受け、質と量
 * (距離) それぞれの偏差値をユーザー調整可能な重みで合成した「総合偏差値」を採用する
 * (`computeCombinedHensachi`、2026-07-23追加)。重みスライダーを0%にすると質のみ、100%にすると
 * 距離のみのランキングになる。
 */
/** 距離の重みスライダーの既定値 (%)。質と量を五分五分で見る中立な初期値。 */
const DEFAULT_DISTANCE_WEIGHT_PERCENT = 50;

function toDateOrNull(raw: unknown): Date | null {
  if (raw instanceof Date) return raw;
  if (raw && typeof (raw as { toDate?: () => Date }).toDate === 'function') {
    return (raw as { toDate: () => Date }).toDate();
  }
  return null;
}

export function RouteReportDashboardPage() {
  const { districtId } = useParams<{ districtId: string }>();
  const districtState = useDistrict(districtId ?? null);
  const { state: authState } = useAdminAuth();
  const { toast } = useToast();

  const isMaster = authState.role === 'master';
  const isDistrictAdminOfThis =
    authState.role === 'district' && !!districtId && authState.districts.includes(districtId);
  const canRun = isMaster || isDistrictAdminOfThis;
  // docs/25 §10-1: この地区で SV 机上調査が有効化されているか (StreetSurveyPage と同じゲート)。
  const featureEnabled =
    districtState.status === 'ready' && districtState.district.streetView?.analysisEnabled === true;

  const reportsState = useRouteReportSnapshots(districtId ?? null);
  // docs/98 R-97: 選んだ調査で保存した集計だけを比べる (調査の key の無い保存は既定の調査)。
  const surveySelection = useStreetSurveySelection(
    districtState.status === 'ready' ? districtState.district : null,
  );
  const reports = (reportsState.status === 'ready' ? reportsState.reports : []).filter(
    (r) => (r.surveyKey || DEFAULT_STREET_SURVEY_KEY) === surveySelection.surveyKey,
  );

  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [deleteTarget, setDeleteTarget] = useState<RouteReportSnapshotWithId | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [qualityMetric, setQualityMetric] = useState<RouteQualityMetric | null>(null);
  const [distanceWeightPercent, setDistanceWeightPercent] = useState(
    DEFAULT_DISTANCE_WEIGHT_PERCENT,
  );

  // 比較のグラフ・表の集計項目を切り替える (docs/27 §9-3)。既定は保存時のまま。
  const [aggAxisKey, setAggAxisKey] = useState<string | null>(null);
  // 既定は細分類ごと (オーナー指示 2026-09-16)。保存時の集計は大分類のこともあるので、
  // 開いた時点で今の解析結果から集計し直す (「保存時の集計に戻す」で保存値を見られる)。
  const [granularity, setGranularity] = useState<AxisGranularity>('category');
  // 各項目を評価の区分ごとの棒に分ける (オーナー指示 2026-09-16)。
  // 既定で評価ごとに分けて積み上げる (オーナー指示 2026-09-16)。
  const [splitByPolarity, setSplitByPolarity] = useState(true);
  const savedReports = reports.filter((r) => selectedIds.includes(r.id));
  // 集計項目・細かさを変えたときだけ、今の解析結果から集計し直す (評価で分ける表示は保存済みの
  // 内訳で足りるので読み込み不要)。既定は保存時の集計のまま = 地点を読み込まない。
  const recomputing = aggAxisKey !== null || granularity !== 'auto';
  const recomputed = useRecomputedSnapshots(districtId ?? null, savedReports, {
    enabled: recomputing,
    axisKey: aggAxisKey ?? null,
    granularity,
    surveyKey: surveySelection.surveyKey,
  });
  const selectedReports = useMemo(
    () =>
      splitByPolarity
        ? recomputed.reports.map((r) => ({
            ...r,
            axisCounts: expandAxisCountsByPolarity(
              r.axisCounts,
              recomputed.evaluation?.polarityLabels,
            ),
          }))
        : recomputed.reports,
    [recomputed.reports, recomputed.evaluation, splitByPolarity],
  );
  const hasPolarity = recomputed.reports.some((r) => r.axisCounts.some((a) => a.polarity));
  const densities = computeRouteDensities(selectedReports);
  // 「質」の指標: 評価の軸があれば肯定の割合を既定にする (検出件数の多さは「良さ」とは限らない、docs/98 R-98 中)。
  const evaluationAvailable = recomputed.reports.some((r) => r.evaluation);
  const effectiveQualityMetric: RouteQualityMetric =
    qualityMetric ?? (evaluationAvailable ? 'positive-ratio' : 'detections-per-point');
  const hensachiValues = computeCombinedHensachi(
    computeRouteQualityValues(selectedReports, effectiveQualityMetric),
    densities.map((d) => d.lengthM),
    distanceWeightPercent / 100,
  );

  function toggleSelect(id: string, checked: boolean) {
    setSelectedIds((prev) => (checked ? [...prev, id] : prev.filter((x) => x !== id)));
  }

  async function handleDelete() {
    if (!deleteTarget || !districtId || deleting) return;
    setDeleting(true);
    try {
      await deleteRouteReportSnapshot(districtId, deleteTarget.id);
      toast('success', `「${deleteTarget.name}」を削除しました。`);
      setSelectedIds((prev) => prev.filter((id) => id !== deleteTarget.id));
    } catch (e) {
      toast('error', `削除に失敗しました: ${(e as Error).message}`);
    } finally {
      setDeleting(false);
      setDeleteTarget(null);
    }
  }

  const listRows: TableRow[] = reports.map((r) => {
    const createdAt = toDateOrNull(r.createdAt);
    return {
      __rowKey: r.id,
      select: (
        <Checkbox
          label=""
          checked={selectedIds.includes(r.id)}
          onChange={(checked) => toggleSelect(r.id, checked)}
        />
      ),
      name: r.name,
      createdAt: createdAt ? createdAt.toLocaleString('ja-JP') : '—',
      lengthM: `${Math.round(r.lengthM)}m`,
      pointCount: `${r.pointCount}件`,
      actions: (
        <Button size="sm" variant="danger" disabled={!canRun} onClick={() => setDeleteTarget(r)}>
          削除
        </Button>
      ),
    };
  });

  const categoryDensityRows: RouteDensityBarChartRow[] = computeRouteCategoryDensities(
    selectedReports,
  ).map((r) => ({
    key: r.id,
    label: r.name,
    segments: r.segments.map((s) => ({
      key: s.key,
      label: s.label,
      color: s.color,
      value: Math.round(s.valuePerPoint * 100) / 100,
    })),
  }));

  // docs/27 §9: 評価の内訳 (評価軸を持つテンプレートで保存したスナップショットのみ値が入る)。
  const hasEvaluation = selectedReports.some((r) => r.evaluation);
  const polarityLabels = recomputed.evaluation?.polarityLabels ?? POLARITY_LABELS;
  const polarityLabelsText = `${polarityLabels.positive} / ${polarityLabels.negative} / ${polarityLabels.neutral}`;
  const qualityMetricLabel = (metric: RouteQualityMetric) =>
    metric === 'positive-ratio'
      ? `${polarityLabels.positive}の割合（${polarityLabels.positive} ÷ （${polarityLabels.positive} ＋ ${polarityLabels.negative}））`
      : '1地点あたり平均検出件数';
  const comparisonRows: TableRow[] = densities.map((d, i) => {
    const ev = selectedReports.find((r) => r.id === d.id)?.evaluation ?? null;
    return {
      __rowKey: d.id,
      name: d.name,
      lengthM: `${Math.round(d.lengthM)}m`,
      pointCount: `${d.pointCount}件`,
      totalCount: `${d.totalCount}件`,
      densityPer100m: `${d.densityPer100m.toFixed(1)}件`,
      perPointAvg: `${d.perPointAvg.toFixed(2)}件`,
      hensachi: hensachiValues[i]?.toFixed(1) ?? '—',
      evaluation: ev ? formatPolarityCounts(ev, polarityLabels) : '—',
    };
  });

  return (
    <>
      <header className="mb-6">
        <Link
          to={
            districtId
              ? `/districts/${districtId}/street-survey${surveySelection.querySuffix}`
              : '/districts'
          }
          className="text-sm text-primary-600 hover:text-primary-700 hover:underline"
        >
          ← ストリートビュー机上調査
        </Link>
        <h1 className="text-2xl font-bold text-slate-900 mt-2">
          路線比較ダッシュボード
          {districtState.status === 'ready' ? ` — ${districtState.district.name}` : ''}
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          「地図で線を描く」一括解析で保存した路線を一覧・比較します。2件以上選択すると、検出件数を地点数で正規化した上で偏差値比較できます。
        </p>
        {districtId && (
          <div className="mt-3">
            <StreetSurveySurveysPanel
              districtId={districtId}
              district={districtState.status === 'ready' ? districtState.district : null}
              selection={surveySelection}
              canManage={false}
              uid={null}
            />
          </div>
        )}
      </header>

      {!canRun && (
        <Card padding="md" className="border-danger-600 mb-4">
          <p className="text-sm text-danger-700">
            この地区での実行権限がありません (role: {authState.role ?? 'none'}、地区: {districtId}
            )。
          </p>
        </Card>
      )}

      {/* docs/25 §10-1: 未有効化の地区では机上調査そのものを出さない (StreetSurveyPage と揃える)。 */}
      {districtState.status === 'ready' && !featureEnabled ? (
        <Card padding="md" className="border-amber-300 bg-amber-50">
          <p className="text-sm text-amber-800">
            この地区ではストリートビュー机上調査を利用できません。
            {isMaster
              ? '「AI 設定」ページの「ストリートビュー机上調査」から、この地区で実施可能にできます。'
              : '利用するにはマスター管理者による有効化が必要です。'}
          </p>
        </Card>
      ) : (
        <>
          <Card padding="md" className="mb-4">
            <Table
              columns={[
                { key: 'select', label: '選択', className: 'w-12' },
                { key: 'name', label: '路線名' },
                { key: 'createdAt', label: '保存日時' },
                { key: 'lengthM', label: '距離' },
                { key: 'pointCount', label: '地点数' },
                { key: 'actions', label: '' },
              ]}
              rows={listRows}
              emptyMessage="保存済みの路線調査がありません。「一括解析」（地図で線を描く）完了後の「路線として保存」から作成してください。"
            />
          </Card>

          {selectedReports.length >= 2 && (
            <Card padding="md" className="mb-4">
              <h2 className="text-sm font-bold text-slate-700 mb-2">
                比較（{selectedReports.length}件選択中）
              </h2>
              <SnapshotAggregationControls
                axes={recomputed.axes}
                axisKey={aggAxisKey}
                onAxisKeyChange={setAggAxisKey}
                granularity={granularity}
                onGranularityChange={setGranularity}
                splitByPolarity={splitByPolarity}
                onSplitByPolarityChange={setSplitByPolarity}
                hasPolarity={hasPolarity}
                recomputing={recomputing}
                onResetToSaved={() => {
                  setAggAxisKey(null);
                  setGranularity('auto');
                }}
                loading={recomputed.loading}
                missingPointCount={recomputed.missingPointCount}
              />
              <p className="text-xs text-slate-500 mb-2">
                選択した路線の経路（路線ごとに個別の地図）
              </p>
              <RouteComparisonMap
                routes={selectedReports.map((r, i) => ({
                  id: r.id,
                  name: r.name,
                  lineCoordinates: r.lineCoordinates,
                  color: REPORT_PALETTE[i % REPORT_PALETTE.length]!,
                }))}
                className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-4"
              />
              <p className="text-xs text-slate-500 mb-3">
                1地点あたり平均検出件数（選択した路線の中での相対比較、検出要素ごとに色分け）
              </p>
              <RouteDensityBarChart rows={categoryDensityRows} />

              <div className="mt-4 flex flex-wrap items-center gap-2">
                <label
                  className="text-sm font-semibold text-slate-700"
                  htmlFor="route-quality-metric"
                >
                  総合偏差値: 質の指標
                </label>
                <select
                  id="route-quality-metric"
                  className="rounded border border-slate-300 px-2 py-1 text-sm bg-white"
                  style={{ maxWidth: '100%' }}
                  value={effectiveQualityMetric}
                  onChange={(e) => setQualityMetric(e.target.value as RouteQualityMetric)}
                >
                  {evaluationAvailable && (
                    <option value="positive-ratio">{qualityMetricLabel('positive-ratio')}</option>
                  )}
                  <option value="detections-per-point">
                    {qualityMetricLabel('detections-per-point')}
                  </option>
                </select>
              </div>
              <div className="mt-2 flex items-center gap-2">
                <label
                  className="text-sm font-semibold text-slate-700"
                  htmlFor="route-distance-weight"
                >
                  総合偏差値: 距離の重み
                </label>
                <input
                  id="route-distance-weight"
                  type="range"
                  min={0}
                  max={100}
                  step={5}
                  value={distanceWeightPercent}
                  onChange={(e) => setDistanceWeightPercent(Number(e.target.value))}
                  className="w-40"
                />
                <span className="text-xs text-slate-500">
                  質{100 - distanceWeightPercent}% : 距離{distanceWeightPercent}%
                  {distanceWeightPercent === 0 && '（質のみ）'}
                  {distanceWeightPercent === 100 && '（距離のみ）'}
                </span>
              </div>

              <h3 className="text-sm font-bold text-slate-700 mt-5 mb-2">詳細（表）</h3>
              <Table
                columns={[
                  { key: 'name', label: '路線名' },
                  { key: 'lengthM', label: '距離' },
                  { key: 'pointCount', label: '地点数' },
                  { key: 'totalCount', label: '検出件数' },
                  { key: 'perPointAvg', label: '1地点あたり平均件数' },
                  { key: 'densityPer100m', label: '100mあたり件数（参考）' },
                  { key: 'hensachi', label: '総合偏差値' },
                  ...(hasEvaluation
                    ? [{ key: 'evaluation', label: `評価（${polarityLabelsText}）` }]
                    : []),
                ]}
                rows={comparisonRows}
              />
              <p className="text-xs text-slate-400 mt-2">
                総合偏差値は「{qualityMetricLabel(effectiveQualityMetric)}」(質) と「距離」(量)
                それぞれの偏差値を、上のスライダーの重みで合成したものです（平均50・標準偏差10）。
                {effectiveQualityMetric === 'detections-per-point' &&
                  '検出件数の多さは、調査の内容によっては良さを表しません（例: 危険箇所の検出）。'}
                母集団が少ないうちは参考値としてご覧ください。「100mあたり件数」は候補点の生成間隔が路線間で異なると歪むため参考表示に留めています。
              </p>
            </Card>
          )}
        </>
      )}

      <ConfirmDialog
        open={deleteTarget !== null}
        intent="danger"
        title="路線を削除しますか？"
        message={`「${deleteTarget?.name ?? ''}」の保存済みスナップショットを完全に削除します。復元できません。`}
        confirmLabel={deleting ? '削除中…' : '削除する'}
        onConfirm={() => void handleDelete()}
        onCancel={() => setDeleteTarget(null)}
      />
    </>
  );
}
